import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { toFrontendKey } from '@/constants/platforms';
import { formatProvider } from '@/lib/oauthResult';

/**
 * Max background refetches while waiting for the backend to persist a connection.
 *
 * The budget (~2 min) has to outlast the backend, not the redirect: the row is
 * written only by the `sync-account` job, which retries 5x with exponential
 * backoff from 5s (`SocialService.enqueueAccountSync`) — up to ~75s before the
 * last try. The old ~9s budget gave up first and left the platform reading as
 * disconnected until a manual refresh (LIL-580). Change both together.
 */
export const REFRESH_MAX_ATTEMPTS = 40;
/** Delay between those refetches. */
export const REFRESH_RETRY_DELAY = 3000;
/**
 * Budget (~30s) for a connect tab that closed without relaying a result. Long
 * enough for a normal sync; short enough that a cancelled connect doesn't hold
 * the card on "Connecting…" for the full two minutes.
 */
export const CLOSED_TAB_MAX_ATTEMPTS = 10;

/** Lock key for a reconcile that isn't waiting on any particular platform. */
const ANY_PLATFORM = '__any__';

/**
 * Reconciles currently running, keyed by the platform each is waiting for. The
 * value identifies the run: a run that no longer owns its key was stopped (see
 * `stopRefreshing`) or taken over, and must neither keep polling nor touch a
 * newer run's state.
 */
const inFlight = new Map<string, { quiet: boolean }>();

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface RefreshOptions {
  maxAttempts?: number;
  retryDelay?: number;
  /**
   * Give up without the "still finishing" notice. For when we don't know the
   * connect succeeded — the tab closed without relaying, which is also what a
   * cancelled connect looks like. The card still shows "Connecting…" meanwhile,
   * so the user never sees an idle gap mid-connect.
   */
  quiet?: boolean;
}

/**
 * Refetch the connection list until `target` shows up as active, or the attempt
 * budget runs out.
 *
 * One refetch isn't enough: the provider's redirect regularly beats its own
 * webhook, so the list still comes back without the account the user just
 * authorized and the platform reads as disconnected until a manual refresh.
 *
 * `target` is a frontend platform key, or null to reconcile once without
 * waiting for anything in particular (the socket event names no platform).
 */
export async function refreshUntilConnected(
  target: string | null,
  {
    maxAttempts = REFRESH_MAX_ATTEMPTS,
    retryDelay = REFRESH_RETRY_DELAY,
    quiet = false,
  }: RefreshOptions = {}
): Promise<void> {
  // Dedupe concurrent triggers for the same platform; the loop retries anyway.
  // Except a confirmed connect over a quiet poll (cancel, then retry): it takes
  // over, so it gets the full budget and the give-up notice, not the leftovers.
  const lock = target ?? ANY_PLATFORM;
  const running = inFlight.get(lock);
  if (running && (quiet || !running.quiet)) return;
  const run = { quiet };
  inFlight.set(lock, run);
  const isCurrent = () => inFlight.get(lock) === run;
  // Lets the card keep showing "Connecting…" after the connect tab closes,
  // instead of flipping back to "Connect" while the backend is still syncing.
  if (target) setSyncing(target, true);

  try {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (!isCurrent()) return;
      // Always a background reconciliation — the page already did its own
      // non-silent load, so this must never flash the full-screen loader.
      await useIntegrationsStore.getState().fetchConnections({ silent: true });
      if (!target) return;

      const connected = useIntegrationsStore
        .getState()
        .connections.some(c => toFrontendKey(c.platform) === target && c.status === 'active');
      if (connected) return;

      if (attempt < maxAttempts - 1) await sleep(retryDelay);
    }
    if (quiet || !isCurrent()) return;

    // Not a failure we can name — the sync may still land, and the socket event
    // or the next focus refetch will pick it up. Say so instead of going quiet,
    // and tell Sentry so a slow sync shows up before a user reports it.
    toast.info(
      `Still finishing your ${formatProvider(target)} connection. It will show up here shortly.`
    );
    Sentry.captureMessage('social-connect: not persisted within budget', {
      level: 'warning',
      tags: { platform: target },
    });
  } finally {
    if (isCurrent()) {
      inFlight.delete(lock);
      if (target) setSyncing(target, false);
    }
  }
}

/**
 * End a running reconcile for `platform` — the backend refused the connection
 * (plan limit), so it will never show up and the card must stop "Connecting…"
 * now rather than after the full budget, without the "still finishing" notice.
 */
export function stopRefreshing(platform: string): void {
  if (!inFlight.has(platform)) return;
  // Release the key now, not when the stopped run wakes from its sleep, so a
  // reconnect right away gets its own run (and its spinner).
  inFlight.delete(platform);
  setSyncing(platform, false);
}

/** Hover copy for a card whose connect returned but the backend is still syncing. */
export function syncingHint(platformName: string): string {
  return `LiLi is finishing your ${platformName} connection. This can take up to a minute.`;
}

function setSyncing(platform: string, on: boolean) {
  useIntegrationsStore.setState(state => ({
    syncingPlatforms: [
      ...state.syncingPlatforms.filter(p => p !== platform),
      ...(on ? [platform] : []),
    ],
  }));
}
